import React, { useState } from 'react';
import { GameState } from '../types/game';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { LowRunwayWarning } from './LowRunwayWarning';
import { StatsPanel } from './StatsPanel';
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, UsersIcon, ActivityIcon, BarChart2Icon } from 'lucide-react';
interface DashboardProps {
  gameState: GameState;
}
export function Dashboard({
  gameState
}: DashboardProps) {
  const [showStats, setShowStats] = useState(false);
  const formatMoney = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      notation: 'compact'
    }).format(val);
  };
  const isProfitable = gameState.netIncome > 0;
  const burnRate = gameState.expenses - gameState.revenue;
  const runway = burnRate > 0 ? Math.max(0, gameState.cash / burnRate) : Infinity;
  return <div className="space-y-6" data-id="element-24">
      <StatsPanel gameState={gameState} isOpen={showStats} onClose={() => setShowStats(false)} data-id="element-25" />

      {/* Top Accent Bar */}
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" data-id="element-26" />

      {/* Header */}
      <div className="flex items-end justify-between border-b border-border pb-6" data-id="element-27">
        <div data-id="element-28">
          <h1 className="text-3xl font-bold text-text-primary tracking-tight mb-1 flex items-center" data-id="element-29">
            ORBIT
            <span className="ml-3 w-2 h-2 rounded-full bg-primary animate-pulse shadow-glow-teal" data-id="element-30" />
          </h1>
          <div className="flex items-center space-x-3 text-sm font-mono text-text-secondary" data-id="element-31">
            <span className="bg-surface-highlight px-2 py-0.5 rounded text-primary border border-primary/20" data-id="element-32">
              YEAR {Math.ceil(gameState.turn / 4)}
            </span>
            <span data-id="element-33">•</span>
            <span data-id="element-34">Q{(gameState.turn - 1) % 4 + 1}</span>
            <span data-id="element-35">•</span>
            <span data-id="element-36">TURN {gameState.turn}</span>
          </div>
        </div>
        <div className="flex items-end flex-col gap-2" data-id="element-37">
          <div className="text-right" data-id="element-38">
            <div className="text-xs text-text-secondary uppercase tracking-wider mb-1" data-id="element-39">
              Status
            </div>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isProfitable ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`} data-id="element-40">
              {isProfitable ? 'PROFITABLE' : `BURNING ${formatMoney(burnRate)}/Q`}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowStats(true)} className="text-xs" data-id="element-41">
            <BarChart2Icon className="w-3 h-3 mr-1" data-id="element-42" />
            View Stats
          </Button>
        </div>
      </div>

      {/* Warning Banner */}
      <LowRunwayWarning cash={gameState.cash} burnRate={burnRate} data-id="element-43" />

      {/* Hero Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-44">
        <Card className="relative overflow-hidden group border-l-2 border-l-primary/50 hover:border-l-primary transition-colors" data-id="element-45">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-46">
            <DollarSignIcon className="w-12 h-12" data-id="element-47" />
          </div>
          <div className="text-xs text-text-secondary uppercase tracking-wider mb-2" data-id="element-48">
            Cash on Hand
          </div>
          <div className="text-2xl md:text-3xl font-mono font-bold text-text-primary truncate" data-id="element-49">
            {formatMoney(gameState.cash)}
          </div>
          <div className="mt-2 text-xs text-text-secondary" data-id="element-50">
            {runway === Infinity ? 'Infinite Runway' : `${runway.toFixed(1)} Qtrs Runway`}
          </div>
        </Card>

        <Card className="relative overflow-hidden group border-l-2 border-l-success/50 hover:border-l-success transition-colors" data-id="element-51">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-52">
            <TrendingUpIcon className="w-12 h-12" data-id="element-53" />
          </div>
          <div className="text-xs text-text-secondary uppercase tracking-wider mb-2" data-id="element-54">
            Revenue (Qtr)
          </div>
          <div className="text-2xl md:text-3xl font-mono font-bold text-text-primary truncate" data-id="element-55">
            {formatMoney(gameState.revenue)}
          </div>
          <div className="mt-2 text-xs text-success flex items-center" data-id="element-56">
            <ActivityIcon className="w-3 h-3 mr-1" data-id="element-57" />
            Last Qtr
          </div>
        </Card>

        <Card className={`relative overflow-hidden group border-l-2 ${gameState.netIncome >= 0 ? 'border-l-success/50 hover:border-l-success' : 'border-l-danger/50 hover:border-l-danger'} transition-colors`} data-id="element-58">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-59">
            <TrendingDownIcon className="w-12 h-12" data-id="element-60" />
          </div>
          <div className="text-xs text-text-secondary uppercase tracking-wider mb-2" data-id="element-61">
            Net Income
          </div>
          <div className={`text-2xl md:text-3xl font-mono font-bold truncate ${gameState.netIncome >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-62">
            {gameState.netIncome > 0 ? '+' : ''}
            {formatMoney(gameState.netIncome)}
          </div>
          <div className="mt-2 text-xs text-text-secondary" data-id="element-63">
            Margin:{' '}
            {gameState.revenue > 0 ? Math.round(gameState.netIncome / gameState.revenue * 100) : 0}
            %
          </div>
        </Card>

        <Card className="relative overflow-hidden group border-l-2 border-l-primary/50 hover:border-l-primary transition-colors" data-id="element-64">
          <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-65">
            <UsersIcon className="w-12 h-12" data-id="element-66" />
          </div>
          <div className="text-xs text-text-secondary uppercase tracking-wider mb-2" data-id="element-67">
            Headcount
          </div>
          <div className="text-2xl md:text-3xl font-mono font-bold text-text-primary truncate" data-id="element-68">
            {gameState.engineers + gameState.sales}
          </div>
          <div className="mt-2 text-xs text-text-secondary flex space-x-2" data-id="element-69">
            <span className="text-primary" data-id="element-70">{gameState.engineers} Eng</span>
            <span data-id="element-71">•</span>
            <span className="text-secondary" data-id="element-72">{gameState.sales} Sales</span>
          </div>
        </Card>
      </div>

      {/* Mini Chart Area */}
      <Card className="h-48 flex flex-col border-primary/10 hover:border-primary/20 transition-colors" data-id="element-73">
        <div className="flex justify-between items-center mb-4" data-id="element-74">
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider" data-id="element-75">
            Financial Trend (Last 8 Qtrs)
          </h3>
          <div className="flex items-center space-x-4 text-xs" data-id="element-76">
            <div className="flex items-center" data-id="element-77">
              <div className="w-2 h-2 rounded-full bg-primary mr-2 shadow-glow-teal-sm" data-id="element-78" />
              <span className="text-text-secondary" data-id="element-79">Cash</span>
            </div>
            <div className="flex items-center" data-id="element-80">
              <div className="w-2 h-2 rounded-full bg-success mr-2" data-id="element-81" />
              <span className="text-text-secondary" data-id="element-82">Revenue</span>
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-end space-x-1 relative" data-id="element-83">
          {gameState.history.slice(-16).map((h, i, arr) => {
          const maxCash = Math.max(...arr.map(x => x.cash));
          const heightPercent = Math.max(5, h.cash / maxCash * 100);
          return <div key={h.turn} className="flex-1 flex flex-col justify-end group relative h-full" data-id="element-84">
                <div className="w-full bg-primary/20 hover:bg-primary/40 transition-all rounded-t-sm relative border-t border-primary/30" style={{
              height: `${heightPercent}%`
            }} data-id="element-85">
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-surface border border-primary/30 p-2 rounded shadow-glow-teal z-10 whitespace-nowrap" data-id="element-86">
                    <div className="text-xs font-mono text-text-primary" data-id="element-87">
                      Q{h.turn}
                    </div>
                    <div className="text-xs text-primary" data-id="element-88">
                      Cash: {formatMoney(h.cash)}
                    </div>
                    <div className="text-xs text-success" data-id="element-89">
                      Rev: {formatMoney(h.revenue)}
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </Card>
    </div>;
}